import { Info, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HoverTip } from "@/components/ui/HoverTip";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import { Textarea } from "@/components/ui/textarea";
import { LimitNote, Note, NotSavedNote } from "@/components/ui/Note";
import { TEXT_LIMITS } from "@/lib/text-limits";
import type { FigureBrief } from "@/hooks/use-figures";
import { useSelfSavingDraft } from "@/hooks/use-debounced-save";
import { blank } from "@/lib/figure-status";

/**
 * The sentence or two the draft prints for this figure in its Brief Description of Drawings.
 *
 * The attorney's own words, saved as they type and when they leave the field, as the
 * walk-through above it is. Not part of the figure's completeness: an empty card asks for
 * attention, and nothing waits on it. Auto-describe asks Praxis for the sentence from the Full
 * Figure Description, and is held back until that has text, as v0.28 holds it; what Praxis
 * writes replaces what is here, and arrives through the stored description like any other edit.
 */
export function BriefDescriptionCard({
  description,
  groupTitle,
  onSave,
  canDescribe,
  brief,
}: {
  description: string;
  /** The title of the group the figure sits in, which the suggested sentence names. */
  groupTitle: string | null;
  onSave: (description: string) => Promise<void>;
  /** Whether the Full Figure Description has text for Praxis to write from. */
  canDescribe: boolean;
  brief: FigureBrief;
}) {
  const { draft, setDraft, flush, failed, retry } = useSelfSavingDraft(
    description,
    onSave,
    TEXT_LIMITS.figureDescription,
  );
  // Named by its group when it has one; the figure's view joins this once the model has one.
  const suggestion = groupTitle?.trim()
    ? `e.g. a schematic of the ${groupTitle.trim()}…`
    : "A sentence or two on what this figure shows.";

  return (
    <Panel
      as="section"
      pad="snug"
      attention={blank(draft)}
      data-testid="define-brief-card"
      className="mt-3.5 shadow-sm"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <label htmlFor="brief-description">
          <MicroLabel>Brief Figure Description</MicroLabel>
        </label>
        <AutoDescribe canDescribe={canDescribe} brief={brief} />
      </div>
      <Panel
        tone="info"
        pad="tight"
        className="mb-2 flex items-start gap-2 text-[11.5px]"
      >
        <Info className="mt-px size-3.5 flex-none" aria-hidden />
        <span>
          <strong>
            This text <u>will</u> appear in the draft
          </strong>
          , in the Brief Description of Drawings section. A sentence or two
          describing the figure; a more detailed account goes in the Full Figure
          Description section above. The draft prints the label first, so write
          what follows it: &quot;a perspective view of the device.&quot;
        </span>
      </Panel>
      <Textarea
        id="brief-description"
        rows={2}
        value={draft}
        placeholder={suggestion}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={flush}
      />
      <LimitNote
        length={draft.length}
        max={TEXT_LIMITS.figureDescription}
        className="mt-1"
      />
      <NotSavedNote failed={failed} onRetry={retry} className="mt-1" />
      {brief.failure !== null && (
        <Note tone="warning" className="mt-1.5">
          {brief.failure}
        </Note>
      )}
    </Panel>
  );
}

/**
 * The button, as v0.28 draws it: held back with its tip until the Full Figure Description has
 * text. The tip sits on a wrapper, because a disabled button takes no hover or focus.
 */
function AutoDescribe({
  canDescribe,
  brief,
}: {
  canDescribe: boolean;
  brief: FigureBrief;
}) {
  const button = (
    <Button
      type="button"
      variant="secondary"
      size="dense"
      disabled={!canDescribe || brief.describing}
      onClick={brief.describe}
    >
      <Sparkles aria-hidden />
      {brief.describing ? "Describing…" : "Auto-describe"}
    </Button>
  );
  if (canDescribe) return button;
  return (
    <HoverTip
      tip="Complete the Full Figure Description first"
      wide={false}
      align="end"
    >
      {({ triggerProps }) => (
        <span data-testid="auto-describe-held" tabIndex={0} {...triggerProps}>
          {button}
        </span>
      )}
    </HoverTip>
  );
}
